import {
  ElementType,
  Fragment,
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import { observer } from 'mobx-react-lite';
import styled, { CSSObject } from 'styled-components';

import { Button, ButtonProps } from '@components/button';
import { AnimatedCheckbox } from '@components/checkbox';
import { useDeepEqualMemoValue, useLocalStorageState } from '@components/component-helpers';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconRadioChecked } from '@components/icons/radio-checked';
import { IconRadioIdle } from '@components/icons/radio-idle';
import { IconRemove } from '@components/icons/remove';
import { IconReplace } from '@components/icons/replace';
import { IconRestart } from '@components/icons/restart';
import { PopoverButton } from '@components/popover-button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Color, theme } from '@styles/theme';
import { match } from '@utils/match';
import { randomId } from '@utils/strings';
import { isFilled } from '@utils/types';

enum CurrentFilterActionType {
  SelectCategory,
  SelectComparer,
  SelectField,
  SelectValue,
  UnselectCategory,
  UnselectComparer,
  UnselectField,
}

enum IFiltersAction {
  ExpandFilters,
  CollapseFilters,
  ToggleFilters,
  ClearFilters,
  RemoveFilter,
  AddFilter,
  UpdateFilter,
  SwitchConjunction,
}

export type GetConjunctionProps = (conjunction: FiltersConjunctionType) => {
  label: string;
  allowSwitch: boolean;
};

const FilterContext = createContext({} as IFilterContext);
const CurrentFilterContext = createContext({} as ICurrentFilterContext);

type FiltersProps = {
  state: FilterState;
  actions: FiltersActionsDefinition;
  filterConfigs: Array<Array<FilterConfig>>;
  filterMap: FilterMap;

  addFilterText: string;
  clearFiltersText: string;
  showingCountText: string;

  getConjunctionProps: GetConjunctionProps;
};

type IFilterContext = Pick<
  FiltersProps,
  'state' | 'actions' | 'filterConfigs' | 'filterMap' | 'getConjunctionProps'
>;
function Filters(props: FiltersProps) {
  const { actions, state, addFilterText, clearFiltersText, showingCountText } = props;

  if (!state.isOpen) return null;

  return (
    <FilterContext.Provider value={props}>
      <Container>
        <Stack
          space={4}
          orientation="horizontal"
          align="center"
          style={{ height: theme.base.height }}
        >
          <Text
            font="regular14"
            color="textHelper"
            text={showingCountText}
            style={{ whiteSpace: 'nowrap' }}
          />
          {state.appliedFilters.length > 0 && (
            <Button
              variant="secondary"
              iconLeft={<IconRestart />}
              text={clearFiltersText}
              onClick={actions.onClearFilters}
            />
          )}
        </Stack>
        <Stack space={2} style={{ minWidth: 0 }}>
          {state.appliedFilters.length > 0 ? (
            <AppliedFiltersList appliedFilters={state.appliedFilters} />
          ) : (
            <Filter
              buttonProps={{
                iconLeft: <IconAddPlus />,
                text: addFilterText,
                variant: 'secondary',
              }}
            />
          )}
        </Stack>
      </Container>
    </FilterContext.Provider>
  );
}

const CurrentFilterIdContext = createContext<string>('');

function AppliedFiltersList(props: { appliedFilters: Array<FilterData> }) {
  const { appliedFilters } = props;
  const { filterMap, getConjunctionProps, state, actions } = useContext(FilterContext);
  const { allowSwitch, label } = getConjunctionProps(state.conjunction);
  return (
    <Fragment>
      {appliedFilters.map((filter, index) => {
        const config = filterMap[filter.category];
        if (!config) return null;
        const AppliedFilterComponent = config.AppliedFilter;
        const extraProps = config.extraProps;
        return (
          <Stack orientation="horizontal" space={2} key={'filter-' + filter.id} align="center">
            {index > 0 && (
              <FiltersConjunctionText
                label={label}
                onClick={allowSwitch ? actions.onSwitchConjunction : undefined}
              />
            )}
            <Stack space={2} orientation="horizontal" style={{ minWidth: 0 }}>
              <CurrentFilterIdContext.Provider value={filter.id}>
                <Filter
                  popoverStyle={{ minWidth: 0, display: 'flex' }}
                  appliedFilter={filter}
                  buttonProps={{
                    variant: 'secondary',
                    children: <AppliedFilterComponent data={filter} {...extraProps} />,
                  }}
                />
              </CurrentFilterIdContext.Provider>
              {index === appliedFilters.length - 1 && (
                <Filter buttonProps={{ variant: 'secondary', iconLeft: <IconAddPlus /> }} />
              )}
            </Stack>
          </Stack>
        );
      })}
    </Fragment>
  );
}

function FiltersConjunctionText(props: { label: string; onClick?: () => void }) {
  if (props.onClick) {
    return (
      <Button variant="secondary" onClick={props.onClick}>
        {props.label}
      </Button>
    );
  }
  return <Text font="semibold14" color="textSecondary" text={props.label} />;
}

function FilterListMenu(props: { onSelect: (category: string) => void }) {
  const { onSelect } = props;
  const { filterConfigs } = useContext(FilterContext);
  const { actions } = useContext(CurrentFilterContext);

  let quickFilters: Array<QuickFilter> = [];
  filterConfigs.forEach((group) => {
    group.forEach((filter) => {
      if (filter.quickFilters) {
        quickFilters = quickFilters.concat(filter.quickFilters);
      }
    });
  });

  return (
    <Fragment>
      {quickFilters.map((quickFilter, index) => (
        <StyledMenuItem key={index} onClick={() => actions.applyFilter(quickFilter.data)}>
          {quickFilter.icon}
          <span>{quickFilter.label}</span>
        </StyledMenuItem>
      ))}
      {quickFilters.length > 0 && <FilterListSeparator />}
      {filterConfigs.map((group, index) => (
        <Fragment key={index}>
          {index !== 0 && <FilterListSeparator key={`separator-${index}`} />}
          {group.map((filter) => (
            <StyledMenuItem key={filter.category} onClick={() => onSelect(filter.category)}>
              {filter.icon}
              <span>{filter.label}</span>
            </StyledMenuItem>
          ))}
        </Fragment>
      ))}
    </Fragment>
  );
}

function useCurrentFilter() {
  return useContext(CurrentFilterContext);
}

function FilterProvider({
  initialData,
  closeMenu,
}: {
  initialData?: FilterData;
  closeMenu(): void;
}) {
  const [state, dispatch] = useReducer(currentFilterStateReducer, {
    ...initialData,
  } as CurrentFilterState);

  const {
    filterMap,
    actions: { onAddFilter, onUpdateFilter, onRemoveFilter },
  } = useContext(FilterContext);

  const actions: CurrentFilterActions = {
    selectCategory: (data) => dispatch({ type: CurrentFilterActionType.SelectCategory, data }),
    selectComparer: (data) => dispatch({ type: CurrentFilterActionType.SelectComparer, data }),
    selectValue: (data) => dispatch({ type: CurrentFilterActionType.SelectValue, data }),
    selectField: (data) => dispatch({ type: CurrentFilterActionType.SelectField, data }),
    unselectField: () => dispatch({ type: CurrentFilterActionType.UnselectField }),
    unselectCategory: () => dispatch({ type: CurrentFilterActionType.UnselectCategory }),
    unselectComparer: () => dispatch({ type: CurrentFilterActionType.UnselectComparer }),
    applyFilter: (data) => {
      const category = state.category || data?.category;
      if (!category) return;

      const method = state.id === undefined ? onAddFilter : onUpdateFilter;
      method({
        id: state.id || randomId(),
        category: category,
        comparer: state.comparer || data?.comparer,
        field: state.field || data?.field,
        value: state.value || data?.value,
      });

      closeMenu();
    },
    removeFilter: () => {
      if (state.id !== undefined) {
        onRemoveFilter(state.id);
      }
    },
  };

  const isMultiComparer = state.comparer ? multiSelectComparers.includes(state.comparer) : false;

  const isEditing = state.id !== undefined;

  const hasValue: boolean = useMemo(() => {
    if (Array.isArray(state.value)) {
      return state.value.length > 0;
    } else {
      return Boolean(state.value);
    }
  }, [state]);

  const contextValue = { state, actions, isMultiComparer, hasValue, isEditing };

  if (!state.category) {
    return (
      <CurrentFilterContext.Provider value={contextValue}>
        <FilterListMenu onSelect={actions.selectCategory} />
      </CurrentFilterContext.Provider>
    );
  }

  const chosenFilter = filterMap[state.category];
  const FilterMenu = chosenFilter.Menu;
  const extraProps = chosenFilter.extraProps;

  return (
    <CurrentFilterContext.Provider value={contextValue}>
      <FilterMenu {...(extraProps as any)} />
    </CurrentFilterContext.Provider>
  );
}

function Filter(props: {
  buttonProps: ButtonProps;
  appliedFilter?: FilterData;
  popoverStyle?: CSSObject;
}) {
  const { buttonProps, appliedFilter, popoverStyle } = props;

  return (
    <PopoverButton
      triggerProps={buttonProps}
      contentStyle={{ maxHeight: 'none', maxWidth: '19.25rem' }}
      popoverStyle={popoverStyle}
    >
      {({ close, isOpen }) => (
        <FilterProvider key={String(isOpen)} initialData={appliedFilter} closeMenu={close} />
      )}
    </PopoverButton>
  );
}

function FiltersAppliedFilter(props: {
  icon: ReactElement<IconProps>;
  category: string;
  comparer?: {
    label: string;
    value: FilterComparer;
  };
  value?: string | string[];
}) {
  const { icon, category, comparer, value } = props;
  const { actions } = useContext(FilterContext);
  const id = useContext(CurrentFilterIdContext);

  return (
    <Stack
      orientation="horizontal"
      justify="space-between"
      space={2}
      as="span"
      style={{ minWidth: 0 }}
    >
      <Stack orientation="horizontal" space={1} align="center" as="span" style={{ minWidth: 0 }}>
        {icon}
        <CategoryText text={category} />
        <Text font="semibold14" color="textPrimary" text={<>&bull;</>} />

        {comparer && (
          <Fragment>
            <ComparerText color={comparerToFilterColor(comparer.value)} text={comparer.label} />
            {value && <Text font="semibold14" color="textPrimary" text={<>&bull;</>} />}
          </Fragment>
        )}
        {value && <ValueText text={Array.isArray(value) ? value.join(', ') : value} />}
        <IconCloseRegular onClick={() => actions.onRemoveFilter(id)} />
      </Stack>
    </Stack>
  );
}

function FilterList(props: { children: ReactNode }) {
  const { children } = props;

  return <div>{children}</div>;
}

function FilterListItem(props: {
  icon?: ReactElement<IconProps>;
  label: ReactNode;
  onClick(): void;
  heading?: boolean;
}) {
  const { onClick, icon, label, heading = false } = props;
  return (
    <StyledMenuItem onClick={onClick} $heading={heading}>
      {icon}
      <span>{label}</span>
    </StyledMenuItem>
  );
}

function FilterSubHeading({ text }: { text: string }) {
  return (
    <div style={{ minHeight: '2.5rem', display: 'flex', alignItems: 'center' }}>
      <Text font="bold14" color="textPrimary" text={text} />
    </div>
  );
}

function FilterValueMenu(props: {
  state: CurrentFilterState;
  items: Array<{ label: string | ReactElement; value: string }>;
  isMulti: boolean;
  onSetValue: CurrentFilterActions['selectValue'];
}) {
  const { items, state, isMulti, onSetValue } = props;

  return (
    <Fragment>
      {items.map((item) => {
        let isSelected = false;
        if (state.value !== undefined) {
          isSelected = isMulti
            ? (state.value as Array<string>).includes(item.value)
            : state.value === item.value;
        }
        return (
          <Filters.ListItem
            key={item.value}
            icon={<FilterSelectionIcon isSelected={isSelected} />}
            onClick={() => onSetValue(item.value)}
            label={item.label}
          />
        );
      })}
    </Fragment>
  );
}

function FiltersActionsGroup(props: { children: ReactNode }) {
  return (
    <FiltersActionsGroupContainer space={4}>
      <FilterListSeparator />
      <Stack orientation="horizontal" space={2} align="center" justify="flex-end">
        {props.children}
      </Stack>
    </FiltersActionsGroupContainer>
  );
}

const FiltersActionsGroupContainer = styled(Stack)({
  position: 'sticky',
  bottom: '-1rem',
  zIndex: 999,
  padding: '0rem 1rem 1rem 1rem',
  margin: '1rem -1rem -1rem -1rem',
  '> div:first-child': {
    margin: 0,
  },
});

function FilterActionButtons(props: {
  applyFilterText: string;
  updateFilterText: string;
  removeFilterText: string;
  disableApply: boolean;
}) {
  const { applyFilterText, removeFilterText, updateFilterText, disableApply } = props;
  const { actions, isEditing } = useCurrentFilter();

  if (!isEditing) {
    return (
      <FiltersActionsGroup>
        <Button
          isDisabled={disableApply}
          variant="primary"
          text={applyFilterText}
          onClick={() => {
            actions.applyFilter();
          }}
        />
      </FiltersActionsGroup>
    );
  }

  return (
    <FiltersActionsGroup>
      <Button
        variant="secondary"
        iconLeft={<IconRemove />}
        text={removeFilterText}
        onClick={() => {
          actions.removeFilter();
        }}
      />

      <Button
        isDisabled={disableApply}
        variant="primary"
        text={updateFilterText}
        iconLeft={<IconReplace />}
        onClick={() => {
          actions.applyFilter();
        }}
      />
    </FiltersActionsGroup>
  );
}

export function FilterSelectionIcon({ isSelected }: { isSelected: boolean }) {
  const { isMultiComparer } = useCurrentFilter();
  return isMultiComparer ? (
    <AnimatedCheckbox checked={isSelected} />
  ) : isSelected ? (
    <IconRadioChecked color="textPrimary" />
  ) : (
    <IconRadioIdle color="textSecondary" />
  );
}

const filtersReducers: {
  [P in IFiltersAction]: (
    state: FilterState,
    action: Extract<IFilterAction, { type: P }>,
  ) => FilterState;
} = {
  [IFiltersAction.AddFilter]: (state, action) => {
    return {
      ...state,
      appliedFilters: [...state.appliedFilters, action.data],
    };
  },
  [IFiltersAction.ClearFilters]: (state) => {
    return {
      ...state,
      appliedFilters: [],
      conjunction: 'and',
    };
  },
  [IFiltersAction.UpdateFilter]: (state, action) => {
    return {
      ...state,
      appliedFilters: state.appliedFilters.map((f) => {
        return { ...(f.id === action.data.id ? action.data : f) };
      }),
    };
  },
  [IFiltersAction.RemoveFilter]: (state, action) => {
    return {
      ...state,
      appliedFilters: state.appliedFilters.filter((f) => f.id !== action.data),
    };
  },
  [IFiltersAction.ToggleFilters]: (state) => {
    return {
      ...state,
      isOpen: !state.isOpen,
    };
  },
  [IFiltersAction.CollapseFilters]: (state) => {
    return {
      ...state,
      isOpen: false,
    };
  },
  [IFiltersAction.ExpandFilters]: (state) => {
    return {
      ...state,
      isOpen: true,
    };
  },
  [IFiltersAction.SwitchConjunction]: (state) => {
    return {
      ...state,
      conjunction: state.conjunction === 'and' ? 'or' : 'and',
    };
  },
};

export type UseFiltersValue = {
  state: FilterState;
  actions: FiltersActionsDefinition;
  filterConfigs: Array<Array<FilterConfig>>;
  filterMap: FilterMap;
  queryData: FilterQueryData;
};

function useFilters(params: {
  filterConfigs: Array<Array<FilterConfig | null>>;
  storageKey?: string;
  onFilterChange?(filterState: Omit<FilterState, 'isOpen'>): void;
  onFilterClear?(): void;
  initialState?: FilterState;
}): UseFiltersValue {
  const {
    filterConfigs: userFilterConfigs,
    storageKey = null,
    onFilterChange,
    onFilterClear,
    initialState = {
      appliedFilters: [],
      conjunction: 'and',
      isOpen: true,
    },
  } = params;

  const isMounted = useRef(false);

  const withoutEmptySections = useMemo(() => {
    return userFilterConfigs
      .map((config) => config.filter(isFilled))
      .filter((config) => config.length > 0);
  }, [userFilterConfigs]);

  const [storedValues, setStoredValues] = useLocalStorageState<FilterState>({
    key: storageKey,
    defaultValue: initialState,
  });

  const [state, dispatch] = useReducer(filtersStateReducer, storedValues);

  const actions: FiltersActionsDefinition = useMemo(() => {
    return {
      onClearFilters: () => {
        dispatch({ type: IFiltersAction.ClearFilters });
        onFilterClear?.();
      },
      onToggleFilters: () => dispatch({ type: IFiltersAction.ToggleFilters }),
      onAddFilter: (data: FilterData) => dispatch({ type: IFiltersAction.AddFilter, data }),
      onUpdateFilter: (data: FilterData) => dispatch({ type: IFiltersAction.UpdateFilter, data }),
      onRemoveFilter: (id: string) => dispatch({ type: IFiltersAction.RemoveFilter, data: id }),
      onSwitchConjunction: () => dispatch({ type: IFiltersAction.SwitchConjunction }),
    };
  }, [onFilterClear]);

  const filterMap = useMemo(() => {
    let obj: FilterMap = {};

    for (let i = 0; i < withoutEmptySections.length; i++) {
      const group = withoutEmptySections[i];

      for (let j = 0; j < group.length; j++) {
        obj[group[j].category] = group[j];
      }
    }
    return obj;
  }, [withoutEmptySections]);

  const _queryData = useMemo(() => {
    let obj: FilterQueryData = {};

    for (let i = 0; i < state.appliedFilters.length; i++) {
      const applied = state.appliedFilters[i];
      const filterConfig = filterMap[applied.category];
      if (!filterConfig) continue;
      const filterQueryData = filterConfig.toQueryData(applied);
      for (const filterKey in filterQueryData) {
        const value = filterQueryData[filterKey];

        if (obj[filterKey] === undefined) obj[filterKey] = [];

        obj[filterKey] = obj[filterKey].concat(value);
      }
    }
    return obj;
  }, [state.appliedFilters, filterMap]);

  // Make sure a new object with the same query data doesn't result in a new
  // query object and thus a new query for the same results.
  const queryData = useDeepEqualMemoValue(_queryData) as FilterQueryData;

  useEffect(() => {
    if (isMounted.current) {
      onFilterChange?.({ appliedFilters: state.appliedFilters, conjunction: state.conjunction });
    }
  }, [onFilterChange, state.appliedFilters, state.conjunction]);

  useEffect(() => {
    setStoredValues(state);
  }, [state, setStoredValues]);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  return {
    state,
    queryData,
    filterMap,
    filterConfigs: withoutEmptySections,
    actions,
  };
}

const currentFilterReducers: {
  [P in CurrentFilterActionType]: (
    state: CurrentFilterState,
    action: Extract<CurrentFilterAction, { type: P }>,
  ) => CurrentFilterState;
} = {
  [CurrentFilterActionType.SelectComparer]: (state, action) => {
    return {
      ...state,
      comparer: action.data,
      value: multiSelectComparers.includes(action.data) ? [] : '',
    };
  },
  [CurrentFilterActionType.UnselectField]: (state) => {
    return {
      ...state,
      field: undefined,
      comparer: undefined,
      value: undefined,
    };
  },
  [CurrentFilterActionType.SelectField]: (state, action) => {
    return {
      ...state,
      field: action.data,
    };
  },
  [CurrentFilterActionType.SelectValue]: (state, action) => {
    const currentValue = state.value;

    if (Array.isArray(currentValue)) {
      if (Array.isArray(action.data)) {
        return {
          ...state,
          value: action.data,
        };
      }

      return {
        ...state,
        value: currentValue.includes(action.data)
          ? currentValue.filter((val) => val !== action.data)
          : [...currentValue, action.data],
      };
    }

    return {
      ...state,
      value: action.data,
    };
  },
  [CurrentFilterActionType.UnselectComparer]: (state) => {
    return {
      ...state,
      comparer: undefined,
      value: undefined,
    };
  },
  [CurrentFilterActionType.SelectCategory]: (state, action) => {
    return {
      ...state,
      category: action.data,
    };
  },
  [CurrentFilterActionType.UnselectCategory]: (state) => {
    return {
      ...state,
      category: undefined,
      comparer: undefined,
      field: undefined,
      value: undefined,
    };
  },
};

function currentFilterStateReducer(state: CurrentFilterState, action: CurrentFilterAction) {
  return match(action.type, currentFilterReducers, state, action);
}

function filtersStateReducer(state: FilterState, action: IFilterAction) {
  return match(action.type, filtersReducers, state, action);
}

function comparerToFilterColor(comparer: FilterComparer): Color {
  switch (comparer) {
    case 'is':
    case 'eq':
    case 'on':
      return 'elementGreen200';
    case 'any_of':
    case 'contains':
      return 'elementOrange200';
    case 'not_is':
    case 'none_of':
    case 'not_contains':
      return 'elementRed200';
    case 'today':
    case 'yesterday':
      return 'elementBlue100';
    default:
      return 'elementOrange200';
  }
}

const multiSelectComparers: Array<FilterComparer> = ['none_of', 'any_of'];

export type FilterValue = string | Array<string>;

type CurrentFilterState = {
  id?: string;
  category?: string;
  comparer?: FilterComparer;
  value?: FilterValue;
  field?: string;
};

type CurrentFilterAction =
  | { type: CurrentFilterActionType.SelectCategory; data: string }
  | { type: CurrentFilterActionType.SelectComparer; data: FilterComparer }
  | { type: CurrentFilterActionType.SelectField; data: string }
  | { type: CurrentFilterActionType.SelectValue; data: string | string[] }
  | { type: CurrentFilterActionType.UnselectCategory }
  | { type: CurrentFilterActionType.UnselectComparer }
  | { type: CurrentFilterActionType.UnselectField };

type CurrentFilterActions = {
  selectComparer(comparer: FilterComparer): void;
  unselectComparer(): void;

  selectCategory(category: string): void;
  unselectCategory(): void;

  selectField(value: string): void;
  unselectField(): void;

  selectValue(value: string | string[]): void;
  applyFilter(overrideData?: Partial<FilterData>): void;
  removeFilter(): void;
};

export type FilterData = {
  id: string;
  category: string;
  value?: FilterValue;
  field?: string;
  comparer?: FilterComparer;
};

type FilterComparerSingle =
  | 'after'
  | 'before'
  | 'between'
  | 'contains'
  | 'eq'
  | 'gt'
  | 'is'
  | 'is_none'
  | 'is_rated_by'
  | 'lt'
  | 'not_contains'
  | 'not_is'
  | 'not_is_rated_by'
  | 'not_starts'
  | 'on'
  | 'starts'
  | 'today'
  | 'yesterday';

type FilterComparerMulti = 'any_of' | 'none_of';

type FilterComparer = FilterComparerSingle | FilterComparerMulti;

type FilterMenuProps<ExtraProps = any> = ExtraProps & {};

type FilterAppliedProps<ExtraProps = any> = {
  data: FilterData;
} & Omit<ExtraProps, 'data'>;

export type FilterQueryData = Record<string, any>;

type QuickFilter = {
  label: string;
  icon: ReactElement<IconProps>;
  data: FilterData;
};

type FilterConfig<ExtraProps = any> = {
  category: string;
  label: string;
  icon: ReactElement<IconProps>;
  Menu: ElementType<FilterMenuProps<ExtraProps>>;
  AppliedFilter: ElementType<FilterAppliedProps<ExtraProps>>;
  toQueryData: (data: FilterData) => FilterQueryData;
  extraProps?: ExtraProps;
  quickFilters?: Array<QuickFilter>;
};

type FilterMap = Record<FilterConfig['category'], FilterConfig>;

type FiltersActionsDefinition = {
  onClearFilters(): void;
  onToggleFilters(): void;

  onAddFilter(data: FilterData): void;
  onUpdateFilter(data: FilterData): void;
  onRemoveFilter(data: string): void;
  onSwitchConjunction(): void;
};

export type FilterState = {
  appliedFilters: Array<FilterData>;
  isOpen: boolean;
  conjunction: FiltersConjunctionType;
};

export type FiltersConjunctionType = 'and' | 'or';

type IFilterAction =
  | { type: IFiltersAction.AddFilter; data: FilterData }
  | { type: IFiltersAction.UpdateFilter; data: FilterData }
  | { type: IFiltersAction.RemoveFilter; data: string }
  | { type: IFiltersAction.ClearFilters }
  | { type: IFiltersAction.ExpandFilters }
  | { type: IFiltersAction.CollapseFilters }
  | { type: IFiltersAction.ToggleFilters }
  | { type: IFiltersAction.SwitchConjunction };

type ICurrentFilterContext = {
  state: CurrentFilterState;
  actions: CurrentFilterActions;
  hasValue: boolean;
  isEditing: boolean;
  isMultiComparer: boolean;
};

const Container = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row-reverse',
  gap: '1rem',
  background: theme.color.backgroundLightDark,
  borderBottom: '0.0625rem solid ' + theme.color.border10,
  padding: '1rem 1.5rem',
  '@media(max-width: 720px)': {
    flexDirection: 'column',
  },
});

const StyledMenuItem = styled.li<{ $heading?: boolean }>((p) => ({
  minHeight: theme.base.height,
  flexShrink: 0,
  font: p.$heading ? theme.font.bold14 : theme.font.regular14,
  display: 'flex',
  alignItems: 'center',
  color: theme.color.textPrimary,
  padding: '0.5rem',
  cursor: 'pointer',
  borderRadius: '0.25rem',

  // A custom property rather than styling the svg, so an icon passed with its
  // own `color` still wins.
  '--icon-color': theme.color.elementPurple80,

  svg: {
    flexShrink: 0,
  },

  '> span': {
    marginLeft: '0.5rem',
    maxWidth: '14rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  '&:hover': {
    backgroundColor: theme.color.uiTrans5,
  },
}));

const FilterListSeparator = styled.div({
  width: '100%',
  height: '0.0625rem',
  backgroundColor: theme.color.border10,
  margin: '1rem 0',
});

const CategoryText = styled(Text).attrs({
  font: 'semibold14',
  color: 'textPrimary',
})({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: '20px',
  flexShrink: 1,
});

const ComparerText = styled(Text).attrs({
  font: 'semibold14',
})({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  flexShrink: 10,
});

const ValueText = styled(Text).attrs({
  font: 'semibold14',
  color: 'elementBlue100',
})({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  flexShrink: 50,
});

Filters.ListSeparator = FilterListSeparator;
Filters.AppliedFilter = FiltersAppliedFilter;
Filters.List = FilterList;
Filters.ListItem = FilterListItem;
Filters.Actions = FilterActionButtons;
Filters.FilterValueMenu = FilterValueMenu;
Filters.SubHeading = FilterSubHeading;
Filters.SelectionIcon = FilterSelectionIcon;

const _Filters = observer(Filters);
export { _Filters as Filters, useCurrentFilter, useFilters };

export type { FilterAppliedProps, FilterComparer, FilterConfig, FilterMenuProps, FiltersProps };
